import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Mail, AlertCircle, Eye, EyeOff } from "lucide-react";
import { auth, rtdb } from "../config/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { ref, get } from "firebase/database";
import toast, { Toaster } from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";

function LoginModal({ isOpen, onClose, onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const captchaRef = useRef(null);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setError("");
    setLoading(false);
    setCaptchaVerified(false);

    // Reset captcha aman
    setTimeout(() => {
      if (
        captchaRef.current &&
        typeof captchaRef.current.reset === "function"
      ) {
        try {
          captchaRef.current.reset();
        } catch (err) {
          console.warn("reCAPTCHA reset skipped:", err.message);
        }
      }
    }, 300);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    if (!isOpen) resetForm();
  }, [isOpen]);

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaVerified(true);
      toast.success("Verifikasi reCAPTCHA berhasil ✅");
    } else {
      setCaptchaVerified(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!captchaVerified) {
      setError("Silakan verifikasi reCAPTCHA terlebih dahulu.");
      toast.error("Verifikasi reCAPTCHA belum dilakukan.");
      return;
    }

    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      const uid = credential.user.uid;
      const snap = await get(ref(rtdb, `akun/${uid}`));

      if (!snap.exists()) throw new Error("Data akun tidak ditemukan.");
      const user = snap.val();

      if (!user.role || user.role.toLowerCase() !== "admin")
        throw new Error("Data akun tidak ditemukan.");

      localStorage.setItem("uid", uid);
      localStorage.setItem("isAdmin", "true");
      localStorage.setItem("username", user.nama || user.email || "");

      toast.success("Login berhasil!");
      onLogin(user);
      onClose();
      resetForm();
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.code?.includes("auth/")
          ? "Email atau password salah."
          : err.message,
      );
      toast.error("Login gagal. Cek kembali kredensial Anda.");
    } finally {
      setLoading(false);
      if (captchaRef.current) captchaRef.current.reset();
      setCaptchaVerified(false);
    }
  };

  const modalVariants = {
    hidden: { scale: 0.9, opacity: 0, y: 50 },
    visible: { scale: 1, opacity: 1, y: 0 },
    exit: { scale: 0.9, opacity: 0, y: 50 },
    transition: { type: "spring", stiffness: 400, damping: 25 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => {
            onClose();
            resetForm();
          }}
        >
          {/* ✅ Toast Global di Modal */}
          <Toaster
            position="top-right"
            containerStyle={{ zIndex: 99999 }}
            toastOptions={{
              style: {
                background: "#fff",
                color: "#111",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "14px",
              },
            }}
          />

          {/* === CARD LOGIN === */}
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            className="relative w-[85%] max-w-sm rounded-2xl p-6 
                       shadow-[0_8px_32px_rgba(0,0,0,0.25)] 
                       bg-gradient-to-br from-white/40 via-white/30 to-white/20 
                       backdrop-blur-3xl border border-white/20"
          >
            {/* Tombol Close */}
            <button
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="absolute top-3 right-3 text-gray-700/80 hover:text-red-500 transition duration-300"
            >
              <X size={22} />
            </button>

            {/* HEADER */}
            <div className="flex items-center justify-center gap-3 mb-6 mt-2">
              <div className="p-3 bg-gradient-to-r from-green-500 to-teal-500 rounded-full shadow-md">
                <Lock size={22} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 tracking-wide">
                Login
              </h2>
            </div>

            {/* FORM */}
            <form onSubmit={handleLogin} className="flex flex-col space-y-4">
              {/* Input Email */}
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700/70"
                />
                <input
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/60 border border-gray-300/30 text-gray-800 placeholder-gray-500
                             px-12 py-3 rounded-lg focus:ring-2 focus:ring-green-400
                             outline-none transition"
                  required
                />
              </div>

              {/* Input Password dengan Toggle */}
              <div className="relative">
                <Lock
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700/70"
                />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/60 border border-gray-300/30 text-gray-800 placeholder-gray-500
                             px-12 py-3 rounded-lg focus:ring-2 focus:ring-green-400
                             outline-none transition pr-12"
                  required
                />
                {/* 👁️ Toggle Show/Hide Password */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-green-600 transition"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* === reCAPTCHA === */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-center mt-2"
              >
                {siteKey ? (
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={siteKey}
                    onChange={handleCaptchaChange}
                    theme="light"
                  />
                ) : (
                  <p className="text-red-500 text-sm text-center">
                    ⚠️ reCAPTCHA belum dikonfigurasi (sitekey kosong)
                  </p>
                )}
              </motion.div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: "auto", marginTop: 4 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    className="bg-red-500/10 border border-red-400/30 text-red-500 text-sm px-4 py-2 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} />
                      <span>{error}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tombol Login */}
              <motion.button
                type="submit"
                disabled={loading || !captchaVerified}
                className={`w-full font-semibold py-3 rounded-lg shadow-md transition-all duration-300
                  ${
                    captchaVerified
                      ? "bg-gradient-to-r from-green-500 to-teal-500 text-white hover:shadow-lg"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                whileHover={captchaVerified ? { scale: 1.03, y: -2 } : {}}
                whileTap={captchaVerified ? { scale: 0.97 } : {}}
              >
                {loading
                  ? "Memeriksa..."
                  : captchaVerified
                    ? "Masuk"
                    : "Verifikasi reCAPTCHA dulu"}
              </motion.button>

              {/* Tombol Batal */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="w-full text-gray-700 py-2 rounded-lg hover:bg-green-50/50 transition"
              >
                Batal
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default LoginModal;
